import React, { useState, useEffect, useRef } from "react";
import { Search, MapPin } from "lucide-react";
import API_BASE_URL from "../services/apiconfig";
import { useLanguage } from '../context/LanguageContext';

export default function FindBranches() {
  const { language } = useLanguage();
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [societyType, setSocietyType] = useState("");
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [societyTypes, setSocietyTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState("");
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);



  const content = {
    en: {
      title: "Find Our Branches",
      district: "District",
      block: "Block",
      optional: "(Optional)",
      societyType: "Society Type",
      selectDistrict: "Select District",
      selectBlock: "Select Block",
      selectSocietyType: "Select Society Type",
      loading: "LOADING...",
      filter: "FILTER",
      required: "*",
      openInGoogleMaps: "Open in Google Maps",
      accuracy: "Accuracy",
      noDataFound: "No branches found for the selected criteria",
      selectFilters: "Please select district and society type, then click Filter to view branches"
    },
    ta: {
      title: "எங்கள் கிளைகளைக் கண்டறியவும்",
      district: "மாவட்டம்",
      block: "வட்டம்",
      optional: "(விருப்பத்தேர்வு)",
      societyType: "சங்க வகை",
      selectDistrict: "மாவட்டத்தைத் தேர்ந்தெடுக்கவும்",
      selectBlock: "வட்டத்தைத் தேர்ந்தெடுக்கவும்",
      selectSocietyType: "சங்க வகையைத் தேர்ந்தெடுக்கவும்",
      loading: "ஏற்றுகிறது...",
      filter: "வடிகட்டு",
      required: "*",
      openInGoogleMaps: "கூகுள் மேப்ஸில் திறக்கவும்",
      accuracy: "துல்லியம்",
      noDataFound: "தேர்ந்தெடுக்கப்பட்ட அளவுகோல்களுக்கு கிளைகள் இல்லை",
      selectFilters: "மாவட்டம் மற்றும் சங்க வகையைத் தேர்ந்தெடுத்து, பின்னர் கிளைகளைப் பார்க்க வடிகட்டு என்பதைக் கிளிக் செய்யவும்"
    }
  };

  const t = content[language];

  useEffect(() => {
    fetchDistricts();
    fetchSocietyTypes();
    loadGoogleMaps();

    return () => {
      // Clear all markers
      if (markersRef.current && markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        markersRef.current = [];
      }
      
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (district) {
      fetchBlocks(district);
      setBlock("");
    }
  }, [district]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      updateMapMarkers();
    }
  }, [locations]);

  const loadGoogleMaps = () => {
    // If already loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      initializeMap();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Wait for it to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          clearInterval(checkGoogleMaps);
          initializeMap();
        }
      }, 100);
      return;
    }

    // Create a unique callback name
    window.initGoogleMap = () => {
      initializeMap();
      delete window.initGoogleMap;
    };

    const script = document.createElement('script');
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&callback=initGoogleMap`;
    script.async = true;
    script.defer = true;
    script.onerror = () => console.error('Error loading Google Maps');
    document.head.appendChild(script);
  };

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;
    
    if (window.google && window.google.maps) {
      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: 11.0168, lng: 78.6569 }, // Tamil Nadu center
          zoom: 7,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true
        });
        
        mapInstanceRef.current = map;
        console.log('Map initialized successfully');
      } catch (error) {
        console.error('Error creating Google Map:', error);
      }
    }
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !window.google) return;

    // Clear existing markers
    if (markersRef.current && markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
    }
    markersRef.current = [];

    // If no locations, reset to Tamil Nadu view
    if (locations.length === 0) {
      mapInstanceRef.current.setCenter({ lat: 11.0168, lng: 78.6569 });
      mapInstanceRef.current.setZoom(7);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const infoWindow = new window.google.maps.InfoWindow();

    // Add new markers
    locations.forEach((location, index) => {
      const lat = parseFloat(location.Latitude);
      const lng = parseFloat(location.Longitude);

      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn(`Invalid coordinates for location ${index}:`, location);
        return;
      }

      const position = { lat, lng };
      
      // Create custom marker
      const marker = new window.google.maps.Marker({
        position: position,
        map: mapInstanceRef.current,
        label: {
          text: `${index + 1}`,
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold'
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 15,
          fillColor: '#dc2626',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2
        }
      });

      // Add click listener
      marker.addListener('click', () => {
        const contentString = `
          <div style="padding: 10px; min-width: 200px;">
            <strong style="font-size: 14px;">${location.Society_Name}</strong><br/>
            <span style="font-size: 12px; color: #666;">
              ${location.Block_Name}, ${location.Taluk_Name}<br/>
              Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}<br/>
              ${t.accuracy}: ${location.Accuracy}m
            </span><br/>
            <a href="https://www.google.com/maps/search/?api=1&query=${lat},${lng}" 
               target="_blank" 
               style="display: inline-block; margin-top: 8px; padding: 6px 12px; background-color: #4285F4; color: white; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 500;">
              ${t.openInGoogleMaps}
            </a>
          </div>
        `;
        
        infoWindow.setContent(contentString);
        infoWindow.open(mapInstanceRef.current, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (locations.length === 1) {
      mapInstanceRef.current.setCenter(bounds.getCenter());
      mapInstanceRef.current.setZoom(13);
    } else if (locations.length > 1) {
      mapInstanceRef.current.fitBounds(bounds);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/master/districts`);
      const result = await response.json();
      if (result.status === "success" && result.data && result.data.districts) {
        const districtData = result.data.districts.map((name) => ({
          id: name,
          name: name
        }));
        setDistricts(districtData);
        if (districtData.length > 0) {
          setDistrict(districtData[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchBlocks = async (districtId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/master/blocks?district=${districtId}`);
      const result = await response.json();
      if (result.status === "success" && result.data && result.data.blocks) {
        const blockData = result.data.blocks.map((name) => ({
          id: name,
          name: name
        }));
        setBlocks(blockData);
      } else {
        setBlocks([]);
      }
    } catch (error) {
      console.error("Error fetching blocks:", error);
      setBlocks([]);
    }
  };

  const fetchSocietyTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/master/society-types`);
      const result = await response.json();
      if (result.status === "success" && result.data && result.data.types) {
        const societyData = result.data.types.map((name) => ({
          id: name,
          name: name
        }));
        setSocietyTypes(societyData);
        if (societyData.length > 0) {
          setSocietyType(societyData[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching society types:", error);
    }
  };

  const fetchLocations = async () => {
    if (!societyType || !district) {
      return;
    }

    setLoading(true);
    setNoDataMessage("");
    
    try {
      let url = `${API_BASE_URL}/master/coordinates?district=${district}&society_type=${societyType}`;
      
      if (block) {
        url += `&block=${block}`;
      }

      const response = await fetch(url);
      const result = await response.json();
      
      if (result.status === "success" && result.data) {
        const locationData = result.data.coordinates || result.data;
        
        if (Array.isArray(locationData) && locationData.length > 0) {
          setLocations(locationData);
          setNoDataMessage("");
        } else {
          setLocations([]);
          setNoDataMessage(t.noDataFound);
        }
      } else {
        setLocations([]);
        setNoDataMessage(t.noDataFound);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
      setNoDataMessage(t.noDataFound);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-8" style={{ backgroundColor: '#e5e5e5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-3">
            {t.title}
          </h2>
          <div className="w-20 h-1 bg-black mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white shadow-lg overflow-hidden border border-gray-300 relative z-0">
              <div className="relative h-64 sm:h-80 lg:h-[450px] w-full">
                <div
                  ref={mapRef}
                  style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}
                  className="z-0"
                ></div>
                
                {/* No Data Overlay */}
                {noDataMessage && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '20px 30px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    textAlign: 'center',
                    border: '2px solid #e5e7eb'
                  }}>
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-700 font-semibold text-base">
                      {noDataMessage}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white shadow-lg p-6 border border-gray-300 space-y-4 flex flex-col">
              <div>
                <label className="block text-gray-800 font-semibold mb-2 text-base">
                  {t.district} <span className="text-red-500">{t.required}</span>
                </label>
                <select
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all duration-200 hover:border-gray-400"
                  disabled={loading}
                >
                  <option value="">{t.selectDistrict}</option>
                  {districts.map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-800 font-semibold mb-2 text-base">
                  {t.block} <span className="text-gray-500 text-xs">{t.optional}</span>
                </label>
                <select
                  value={block}
                  onChange={(e) => setBlock(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all duration-200 hover:border-gray-400"
                  disabled={loading || !district || blocks.length === 0}
                >
                  <option value="">{t.selectBlock}</option>
                  {blocks.map((blk) => (
                    <option key={blk.id} value={blk.id}>
                      {blk.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-800 font-semibold mb-2 text-base">
                  {t.societyType} <span className="text-red-500">{t.required}</span>
                </label>
                <select
                  value={societyType}
                  onChange={(e) => setSocietyType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all duration-200 hover:border-gray-400"
                  disabled={loading}
                >
                  <option value="">{t.selectSocietyType}</option>
                  {societyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-center pt-2 mt-auto">
                <button
                  onClick={fetchLocations}
                  disabled={loading || !district || !societyType}
                  className="bg-gray-800 hover:bg-black text-white font-semibold py-3 px-8 transition-colors duration-200 flex items-center justify-center gap-2 text-sm shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t.loading}
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      {t.filter}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}